import React, { useContext, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { useCookies } from "react-cookie";

import updateCartHandler, { UpdateCartActions } from "./handlers/cart";

import { Cart, LocationMenu, LocationMenuItem, MenuItemOption } from "../types";
import { getMenu } from "../api";
import { CustomerOrderingTranslateMenuRes } from "@sally-pos/pos-core";
import { CustomerOrderingGetMenuRes } from "../types/extensions";
import { getTagIdCookieName, resolveRequestedTagId } from "../utils/tagId";
import { getCartCookieName, getCartCookiePath } from "../utils/cartCookie";

export interface IMenuContext {
  loading: boolean;
  data?: CustomerOrderingGetMenuRes;
  /**
   * The flag the menu was priced under, as confirmed by the server. Send this
   * with the order — never re-read `?tagId=` at the point of ordering, which is
   * several navigations away from the URL it arrived on.
   */
  tagId?: string;
  menu?: LocationMenu;
  items?: Record<string, LocationMenuItem>;
  options?: Record<string, MenuItemOption>;
  cart: Cart;
  translations?: CustomerOrderingTranslateMenuRes;
  updateCart: (type: UpdateCartActions, payload: any) => void;
  setTranslations: (translationsRes: CustomerOrderingTranslateMenuRes) => void;
}

export const MenuContext = React.createContext<IMenuContext>({
  loading: true,
  cart: [],
  updateCart: () => {},
  setTranslations: () => {},
});

const COOKIE_EXPIRY_MS = 2 * 60 * 60 * 1000; // 2 hours

const cookieExpiryDate = new Date();
cookieExpiryDate.setTime(cookieExpiryDate.getTime() + COOKIE_EXPIRY_MS);

const MenuContextWrapper: React.FC<React.PropsWithChildren> = (props) => {
  const { locationId, tableId } = useParams();
  const [searchParams] = useSearchParams();
  const [cookies, setCookies, removeCookie] = useCookies();
  const cartCookie = getCartCookieName(locationId!);

  const [cart, setCart] = useState<Cart>([]);
  const [sessionData, setSessionData] = useState<CustomerOrderingGetMenuRes>();
  const [translations, setTranslations] =
    useState<CustomerOrderingTranslateMenuRes>();
  const [items, setItems] = useState<Record<string, LocationMenuItem>>({});
  const [options, setOptions] = useState<Record<string, MenuItemOption>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    function init() {
      let tempCart = cookies[cartCookie];

      if (tempCart) {
        if (typeof tempCart === "string") {
          try {
            tempCart = JSON.parse(tempCart);
          } catch (err) {
            console.error(
              "Could not parse cart from cookies. Details: ",
              err,
              tempCart,
            );
          }
        }

        if (Array.isArray(tempCart)) {
          setCart(tempCart);
        }
      }
    }

    async function fetchMenuData() {
      try {
        setLoading(true);

        const loaded = cookies.loaded === "true";

        const tagIdCookie = getTagIdCookieName(locationId!);

        const requestedTagId = resolveRequestedTagId(
          searchParams.get("tagId"),
          cookies[tagIdCookie],
        );

        const response = await getMenu({
          locationId: locationId!,
          tableId,
          loaded,
          tagId: requestedTagId,
        });

        // Remember the flag only when this customer actually asked for one AND
        // the server honoured it. Persisting whatever came back would also
        // freeze the location's own default into a cookie, so changing that
        // setting wouldn't reach anyone mid-session; and a rejected flag would
        // be replayed on every later request.
        if (requestedTagId && response.tagId === requestedTagId) {
          setCookies(tagIdCookie, requestedTagId, {
            expires: cookieExpiryDate,
          });
        } else if (cookies[tagIdCookie]) {
          setCookies(tagIdCookie, "");
        }

        const items: Record<string, LocationMenuItem> = {};
        const options: Record<string, MenuItemOption> = {};

        response.items?.forEach((item) => {
          if (item) {
            items[item.id] = item;
          }
        });

        response.options?.forEach((option) => {
          if (option?.id) {
            options[option.id] = option;
          }
        });

        setSessionData(response);
        setItems(items);
        setOptions(options);

        setCookies("loaded", true, { expires: cookieExpiryDate });

        if (response.style?.primaryColor) {
          document.documentElement.style.setProperty(
            "--primary-color",
            response.style.primaryColor,
          );
          // A venue colour is chosen to carry a white label, in either theme.
          document.documentElement.style.setProperty(
            "--primary-contrast",
            "#ffffff",
          );
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }

    init();
    fetchMenuData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Always the same path, so a clear reaches the copy every page reads.
    const path = getCartCookiePath(locationId!);

    if (cart.length > 0) {
      setCookies(cartCookie, JSON.stringify(cart), {
        path,
        expires: cookieExpiryDate,
      });
    } else if (cookies[cartCookie]) {
      removeCookie(cartCookie, { path });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart]);

  return (
    <MenuContext.Provider
      value={{
        loading,
        data: sessionData,
        tagId: sessionData?.tagId,
        cart: cart,
        menu: sessionData?.menu.data,
        items,
        options,
        translations,
        updateCart: (type: UpdateCartActions, payload: any) => {
          const c = updateCartHandler(cart, type, payload);

          setCart(c);
        },
        setTranslations: (
          translationsRes: CustomerOrderingTranslateMenuRes,
        ) => {
          setTranslations(translationsRes);
        },
      }}
    >
      {props.children}
    </MenuContext.Provider>
  );
};

export const useMenu = () => {
  return useContext<IMenuContext>(MenuContext);
};

export default MenuContextWrapper;
